/* eslint-disable max-len */
import React, { useEffect, useRef } from "react";
import { isEmpty } from "lodash";
import PropTypes from "prop-types";

import { Formik, FieldArray } from "formik";
import * as Yup from "yup";
import Grid from "@mui/material/Grid";

import {
  Input,
  AutoSaveField as Field,
  SelectAutocomplete,
  InlineRadioGroup,
  CustomSelect
} from "../../../../../../components/Form";
import { Divider } from "../../../../../../components/Divider";
import { ArrayAddButton } from "../../../Buttons/ArrayAddButton";
import { ContinueButton } from "../../../../../../components/Buttons/ContinueButton";
import { YesNoList } from "../../../../../../constants/options";
import { cifUpdateOptions, poaOptions } from "./constants";
import { NUMBER_REGEX, DECIMAL_UPTO_TWO_REGEX } from "../../../../../../utils/validation";
import {
  getInvalidMessage,
  getRequiredMessage
} from "../../../../../../utils/getValidationMessage";
// import { checkIsTrimmed } from "../../../../../../utils/validation";
// import {
//   getInvalidMessage,
//   getRequiredMessage
// } from "../../../../../../utils/getValidationMessage";

import { RelativeBox, ButtonWrapper, RemoveButton } from "./styled";
import { FormContainer } from "../CompanyBusinessRelationships/styled";
import { ScrollToError } from "components/ScrollToError";

export const accountAttributesSchema = isExistingCustomer =>
  Yup.object().shape({
    accountAttributes: Yup.object().shape({
      riskScore: Yup.string()
        .nullable()
        .matches(DECIMAL_UPTO_TWO_REGEX, getInvalidMessage("Risk score")),
      expressCode: Yup.string().nullable(),
      currency: Yup.string().nullable(),
      addressEid: Yup.string().nullable(),
      cifUpdateRequired: Yup.string().nullable(),
      cifUpdateRequiredOn: Yup.string().nullable(),
      employmentChangeRequired: Yup.string().nullable(),
      uidMatch: Yup.string().nullable(),
      highRiskException: Yup.string().nullable(),
      blacklistFound: Yup.string().nullable(),
      uboApproval: Yup.string().nullable(),
      pep: Yup.string().nullable(),
      newsCheck: Yup.string().nullable(),
      nationalityException: Yup.string().nullable(),
      industryException: Yup.string().nullable(),
      demographicException: Yup.string().nullable(),
      dmccException: Yup.string().nullable(),
      arabicDocuments: Yup.string().nullable(),
      rakConnect: Yup.string().nullable(),
      priorityAccount: Yup.string().nullable(),
      poaSignatory: Yup.string().nullable(),
      poaValidation: Yup.string().nullable(),
      domicileSOL: Yup.string().nullable()
    }),
    cifNumbers: Yup.array().of(
      Yup.object().shape({
        cifNumber: Yup.string()
          .nullable()
          .max(7, "Maximum 7 characters allowed")
          .matches(NUMBER_REGEX, getInvalidMessage("CIF Number"))
      })
    ),
    custHaveExistCif: Yup.string()
      .test(
        "required",
        getRequiredMessage("Customer having existing CIF"),
        value => !isExistingCustomer || !!value
      )
      .nullable()
  });

export const initialaccountAttributes = {
  riskScore: "",
  expressCode: "",
  cifUpdateRequired: "",
  cifUpdateRequiredOn: "",
  employmentChangeRequired: "",
  uidMatch: "",
  highRiskException: "",
  blacklistFound: "",
  uboApproval: "",
  pep: "",
  newsCheck: "",
  nationalityException: [],
  industryException: [],
  demographicException: [],
  dmccException: "",
  arabicDocuments: "",
  rakConnect: "",
  priorityAccount: "",
  poaSignatory: "",
  poaValidation: [],
  domicileSOL: "",
  custHaveExistCif: ""
};
export const initialValues = (cifNos, custHaveExist) => {
  return {
    accountAttributes: initialaccountAttributes,
    custHaveExistCif: custHaveExist,
    cifNumbers: !isEmpty(cifNos)
      ? cifNos.map(cifNo => ({
          cifNumber: cifNo
        }))
      : [
          {
            cifNumber: ""
          }
        ]
  };
};

export const CompanyAccountAttributesComponent = ({
  handleContinue,
  createFormChangeHandler,
  updateRoProspect,
  cifNos,
  custHaveExist,
  prospectRiskScore,
  roProspectRiskScore,
  isExistingCustomer
}) => {
  const formRef = useRef();
  const roProspectPath = "roProspect.financeInfo";

  useEffect(() => {
    if (isEmpty(roProspectRiskScore)) {
      formRef.current.setFieldValue("accountAttributes.riskScore", prospectRiskScore);
      formRef.current.setFieldTouched("accountAttributes.riskScore", false);
    }
  }, [prospectRiskScore, roProspectRiskScore]);
  return (
    <Formik
      innerRef={formRef}
      initialValues={initialValues(cifNos, custHaveExist)}
      validationSchema={accountAttributesSchema(isExistingCustomer)}
      onSubmit={handleContinue}
      validateOnChange={false}
    >
      {createFormChangeHandler(({ values, isValid }) => {
        const cifNumbers = values.cifNumbers.length > 1;

        return (
          <FormContainer>
            <ScrollToError />
            <Grid container spacing={3}>
              <Grid item sm={12} xs={12}>
                <h5> BVR details </h5>
              </Grid>
              <Grid item sm={6} xs={12}>
                <Field
                  name="accountAttributes.riskScore"
                  label="Risk Score"
                  path={`${roProspectPath}.riskScore`}
                  placeholder="Risk Score"
                  component={Input}
                  InputProps={{
                    inputProps: {
                      tabIndex: 0,
                      allowNegative: false
                    }
                  }}
                />
              </Grid>
              <Grid item sm={6} xs={12}>
                <Field
                  name="accountAttributes.expressCode"
                  label="Express Code"
                  path={`${roProspectPath}.expressCode`}
                  component={SelectAutocomplete}
                  datalistId="expressCode"
                  disabled
                  shrink
                  tabIndex="0"
                />
              </Grid>
              <Grid item sm={6} xs={12}>
                <Field
                  name={"accountAttributes.currency"}
                  label="Currency"
                  path={`${roProspectPath}.currency`}
                  component={SelectAutocomplete}
                  datalistId="currencyBvr"
                  shrink
                  tabIndex="0"
                />
              </Grid>
              <Grid item sm={6} xs={12}>
                <Field
                  name={"accountAttributes.addressEid"}
                  label="Address/EID"
                  path={`${roProspectPath}.eid`}
                  component={SelectAutocomplete}
                  datalistId="addressEid"
                  shrink
                  tabIndex="0"
                />
              </Grid>
              <Grid item sm={10} xs={9}>
                <Field
                  name={"accountAttributes.cifUpdateRequired"}
                  path={`${roProspectPath}.cifupdateRequired`}
                  label="CIF Update required?"
                  component={InlineRadioGroup}
                  options={YesNoList}
                  inputProps={{ tabIndex: 0 }}
                />
              </Grid>
              {values.accountAttributes.cifUpdateRequired === "Yes" && (
                <Grid item sm={10} xs={9}>
                  <Field
                    name={"accountAttributes.cifUpdateRequiredOn"}
                    label="CIF Update required on Retail/Corp CIF"
                    path={`${roProspectPath}.cifupdateReqdOn`}
                    component={InlineRadioGroup}
                    options={cifUpdateOptions}
                    inputProps={{ tabIndex: 0 }}
                  />
                </Grid>
              )}
              <Grid item sm={12} xs={12}>
                <h5> CIF numbers on which update is required </h5>
                <FieldArray name="cifNumbers">
                  {cifNumbersArrayHelpers => (
                    <>
                      <Grid container spacing={3}>
                        {values.cifNumbers.map((item, index) => (
                          <React.Fragment key={item?.id || index}>
                            <Grid item sm={cifNumbers ? 10 : 12} xs={12} component={RelativeBox}>
                              <Field
                                name={`cifNumbers[${index}].cifNumber`}
                                path={`${roProspectPath}.cifNo[${index}]`}
                                component={Input}
                              />
                            </Grid>
                            {cifNumbers && (
                              <Grid item sm={2} xs={12} alignSelf={"center"}>
                                <RemoveButton
                                  prospectPath="roProspect.financeInfo.cifNo"
                                  arrayHelpers={cifNumbersArrayHelpers}
                                  dataArray={cifNos}
                                  itemIndex={index}
                                  updateProspect={updateRoProspect}
                                  title="Delete"
                                />
                              </Grid>
                            )}
                          </React.Fragment>
                        ))}
                      </Grid>
                      <ArrayAddButton
                        title="Add"
                        limit={3}
                        requiredFields={["cifNumber"]}
                        addedItem={{
                          cifNumber: ""
                        }}
                        dataArray={values.cifNumbers}
                        arrayHelpers={cifNumbersArrayHelpers}
                        noUniqueID={true}
                        sx={{ mt: 1 }}
                      />
                    </>
                  )}
                </FieldArray>
              </Grid>
              <Grid item sm={10} xs={9}>
                <Field
                  name={"accountAttributes.employmentChangeRequired"}
                  label="Is Employment type change required?"
                  path={`${roProspectPath}.empTypeChangeRequired`}
                  component={InlineRadioGroup}
                  options={YesNoList}
                  inputProps={{ tabIndex: 0 }}
                />
              </Grid>
              <Grid item sm={12} xs={12}>
                <h5> Exceptions </h5>
              </Grid>
              <Grid item sm={6} xs={12}>
                <Field
                  name={"accountAttributes.uidMatch"}
                  label="UID Match"
                  path={`${roProspectPath}.uidMatch`}
                  component={SelectAutocomplete}
                  datalistId="YesNoList"
                  shrink
                  tabIndex="0"
                />
              </Grid>
              <Grid item sm={6} xs={12}>
                <Field
                  name={"accountAttributes.highRiskException"}
                  label="High Risk Exception"
                  path={`${roProspectPath}.highRiskException`}
                  component={SelectAutocomplete}
                  datalistId="YesNoList"
                  shrink
                  tabIndex="0"
                />
              </Grid>
              <Grid item sm={6} xs={12}>
                <Field
                  name={"accountAttributes.blacklistFound"}
                  label="Blacklist Found"
                  path={`${roProspectPath}.blackListFound`}
                  component={SelectAutocomplete}
                  datalistId="blacklistFound"
                  shrink
                  tabIndex="0"
                />
              </Grid>
              <Grid item sm={6} xs={12}>
                <Field
                  name={"accountAttributes.uboApproval"}
                  label="UBO Approval"
                  path={`${roProspectPath}.uboApproval`}
                  component={SelectAutocomplete}
                  datalistId="YesNoList"
                  shrink
                  tabIndex="0"
                />
              </Grid>
              <Grid item sm={6} xs={12}>
                <Field
                  name={"accountAttributes.pep"}
                  label="PEP"
                  path={`${roProspectPath}.isPEP`}
                  component={SelectAutocomplete}
                  datalistId="politicalyExposedPerson"
                  shrink
                  tabIndex="0"
                />
              </Grid>
              <Grid item sm={6} xs={12}>
                <Field
                  name={"accountAttributes.newsCheck"}
                  label="News Check"
                  path={`${roProspectPath}.newsCheck`}
                  component={SelectAutocomplete}
                  datalistId="YesNoList"
                  shrink
                  tabIndex="0"
                />
              </Grid>
              <Grid item sm={6} xs={12}>
                <Field
                  multiple
                  name={"accountAttributes.nationalityException"}
                  label="Nationality Exception"
                  path={`${roProspectPath}.nationalityException`}
                  component={SelectAutocomplete}
                  datalistId="nationalityException"
                  shrink
                  tabIndex="0"
                  isSearchable
                  extractValue={value => value}
                />
              </Grid>
              <Grid item sm={6} xs={12}>
                <Field
                  name={"accountAttributes.industryException"}
                  label="Industry Exception"
                  path={`${roProspectPath}.industryException`}
                  component={SelectAutocomplete}
                  datalistId="industryException"
                  shrink
                  tabIndex="0"
                  multiple
                  isSearchable
                  extractValue={value => value}
                />
              </Grid>
              <Grid item sm={6} xs={12}>
                <Field
                  name={"accountAttributes.demographicException"}
                  label="Demographic Exception"
                  path={`${roProspectPath}.demographicException`}
                  component={SelectAutocomplete}
                  datalistId="demographicException"
                  shrink
                  tabIndex="0"
                  multiple
                  isSearchable
                  extractValue={value => value}
                />
              </Grid>
              <Grid item sm={6} xs={12}>
                <Field
                  name={"accountAttributes.dmccException"}
                  label="DMCC Exception"
                  path={`${roProspectPath}.dmccException`}
                  component={SelectAutocomplete}
                  datalistId="YesNoList"
                  shrink
                  tabIndex="0"
                />
              </Grid>
              <Grid item sm={12} xs={12}>
                <h5> BAIS </h5>
              </Grid>
              <Grid item sm={10} xs={9}>
                <Field
                  name={"accountAttributes.arabicDocuments"}
                  label="Arabic Documents"
                  path={`${roProspectPath}.arabicDocuments`}
                  component={InlineRadioGroup}
                  options={YesNoList}
                  inputProps={{ tabIndex: 0 }}
                />
              </Grid>
              <Grid item sm={6} xs={12}>
                <Field
                  name={"accountAttributes.rakConnect"}
                  label="RAK Connect"
                  path={`${roProspectPath}.rakConnect`}
                  component={SelectAutocomplete}
                  datalistId="YesNoList"
                  shrink
                  tabIndex="0"
                />
              </Grid>
              <Grid item sm={6} xs={12}>
                <Field
                  name={"accountAttributes.priorityAccount"}
                  label="Priority Account"
                  path={`${roProspectPath}.priorityAccount`}
                  component={SelectAutocomplete}
                  datalistId="priorityAccount"
                  shrink
                  tabIndex="0"
                />
              </Grid>
              <Grid item sm={10} xs={9}>
                <Field
                  name={"accountAttributes.poaSignatory"}
                  label="POA Signatory"
                  path={`${roProspectPath}.poaSignatory`}
                  component={InlineRadioGroup}
                  options={poaOptions}
                  inputProps={{ tabIndex: 0 }}
                />
              </Grid>
              <Grid item sm={6} xs={12}>
                <Field
                  name={"accountAttributes.poaValidation"}
                  label="POA Validation"
                  path={`${roProspectPath}.poaValidation`}
                  component={SelectAutocomplete}
                  datalistId="poaValidation"
                  shrink
                  tabIndex="0"
                  multiple
                  isSearchable
                  extractValue={value => value}
                />
              </Grid>
              <Grid item sm={6} xs={12}>
                <Field
                  name={"accountAttributes.domicileSOL"}
                  label="Domicile SOL"
                  path={`${roProspectPath}.domicileSOL`}
                  component={CustomSelect}
                  datalistId="domicileSol"
                  shrink
                  tabIndex="0"
                  optional={true}
                />
              </Grid>
              {isExistingCustomer && (
                <Grid item sm={12} xs={12}>
                  <Field
                    name="custHaveExistCif"
                    label="Customer having existing CIF?"
                    component={InlineRadioGroup}
                    path={"prospect.applicationInfo.custHaveExistCif"}
                    datalistId="custHaveExistCif"
                    typeRadio
                    required
                    contextualHelpProps={{ isDisableHoverListener: false }}
                  />
                </Grid>
              )}
            </Grid>
            <Divider.Light />
            <ButtonWrapper>
              <ContinueButton type="submit" disabled={isExistingCustomer && !isValid} />
            </ButtonWrapper>
          </FormContainer>
        );
      })}
    </Formik>
  );
};

CompanyAccountAttributesComponent.propTypes = {
  /**
   * Function to handle form submission and continue to next step
   */
  handleContinue: PropTypes.func.isRequired,

  /**
   * Function to create form change handler with additional functionality
   */
  createFormChangeHandler: PropTypes.func.isRequired,

  /**
   * Function to update RO prospect data in Redux store
   */
  updateRoProspect: PropTypes.func.isRequired,

  /**
   * Array of CIF numbers
   */
  cifNos: PropTypes.arrayOf(PropTypes.string),

  /**
   * Boolean indicating if customer has existing CIF
   */
  custHaveExist: PropTypes.string,

  /**
   * Risk score value for the prospect
   */
  prospectRiskScore: PropTypes.string,

  /**
   * Risk score value for the RO prospect
   */
  roProspectRiskScore: PropTypes.string,

  /**
   * Boolean indicating if the customer is an existing customer
   */
  isExistingCustomer: PropTypes.bool
};
